import { forwardRef, PropsWithChildren } from 'react'

import { css, keyframes } from '@emotion/react'
import styled from '@emotion/styled'
import { createPortal } from 'react-dom'

import { ArcadeModalActionProvider, ArcadeModalHeader, IsInArcadeModalContext } from '@cash-design-system/react'
import { usePreventScroll } from '@cash-design-system/react'
import { useArcadeMigrationFeatureFlag } from '@cash-web/shared-data-access-arcade-migration'

import { ModalActionProvider } from './ModalActionProvider'
import { ModalActions } from './ModalActions'
import { ModalContextProvider } from './ModalContext'
import { useModalSharedButtonProps } from './utils/useModalSharedProps'

const sheetEnter = keyframes`
  0% {
    transform: translateY(100%);
    border-top-left-radius: 1.5rem;
    border-top-right-radius: 1.5rem;
  }
  30% {
    transform: translateY(30%);
    border-top-left-radius: 0;
    border-top-right-radius: 0;
  }
  100% {
    transform: translateY(0);
    border-top-left-radius: 0;
    border-top-right-radius: 0;
  }
`

const Backdrop = styled.div<{ disableAnimation: boolean }>`
  position: absolute;
  inset: 0;
  background-color: rgb(0 0 0 / 50%);
`

const IOS_SHEET_CUBIC_BEZIER = 'cubic-bezier(0.32, 0.72, 0, 1)'
const FullscreenContainer = styled.div<{ disableAnimation: boolean }>`
  z-index: 1;
  position: fixed;
  inset: 0;
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.arcadeColors.component.modalBackground};
  overflow: hidden;
  padding: 0;

  ${({ disableAnimation }) =>
    !disableAnimation &&
    css`
      animation: ${sheetEnter} 0.3s ${IOS_SHEET_CUBIC_BEZIER} forwards;
    `}
`

export const MobileSheetHeader = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
`

const ActionsContainer = styled.div`
  max-height: 4rem;

  &:has(*) {
    padding: ${({ theme }) => `${theme.spacing.xxs} ${theme.spacing.xxs} 0 ${theme.spacing.xxs}`};
  }

  @media (orientation: landscape) {
    max-height: 1rem;
  }
`

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
`

export interface MobileSheetProps {
  onClose?: () => void
  onBack?: () => void
  closable?: boolean
  disableAnimation?: boolean
}

/**
 * A WIP fullscreen sheet component for mobile web experiences.
 *
 * @remarks
 * This component is currently in development to iterate on mobile web flows,
 * specifically Card Studio. It is not intended for general use.
 */
export const MobileSheet = forwardRef<HTMLDivElement, PropsWithChildren<MobileSheetProps>>(
  ({ children, onClose, onBack, closable = true, disableAnimation = false }, ref) => {
    usePreventScroll({ prevent: true })
    const isArcadeMigrationEnabled = useArcadeMigrationFeatureFlag('modal')
    const sharedModalButtonProps = useModalSharedButtonProps({
      closeButtonProps: { onClose },
      backButtonProps: { onBack },
    })

    return createPortal(
      <>
        <Backdrop disableAnimation={disableAnimation} />
        <FullscreenContainer
          disableAnimation={disableAnimation}
          role="dialog"
          aria-modal="true"
          data-testid="mobile-modal"
          ref={ref}
        >
          {isArcadeMigrationEnabled ? (
            <IsInArcadeModalContext.Provider value={true}>
              <ArcadeModalActionProvider>
                <ActionsContainer>
                  {closable ? <ArcadeModalHeader {...sharedModalButtonProps} /> : null}
                </ActionsContainer>
                <Content>{children}</Content>
              </ArcadeModalActionProvider>
            </IsInArcadeModalContext.Provider>
          ) : (
            <ModalContextProvider>
              <ModalActionProvider>
                {closable ? (
                  <ActionsContainer>
                    <ModalActions onClose={onClose} onBack={onBack} />
                  </ActionsContainer>
                ) : null}
                <Content>{children}</Content>
              </ModalActionProvider>
            </ModalContextProvider>
          )}
        </FullscreenContainer>
      </>,
      document.getElementById('modal-root') as HTMLElement
    )
  }
)

MobileSheet.displayName = 'MobileSheet'
